import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LANGUAGES, byCode, parsePath, localizePath } from "@/i18n/config";

export default function LanguageSelector({ variant = "desktop" }) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const current = byCode(parsePath(location.pathname).code);

  const switchTo = (code) => {
    try { localStorage.setItem("uni_lang", code); } catch (e) { /* noop */ }
    const target = localizePath(location.pathname, code) + location.search + location.hash;
    navigate(target);
  };

  if (variant === "mobile") {
    return (
      <div className="pt-2" data-testid="lang-selector-mobile">
        <p className="pl-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">{t("lang.label")}</p>
        <div className="grid grid-cols-2 gap-1.5">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => switchTo(l.code)} lang={l.htmlLang}
              className={`flex items-center justify-between rounded-sm border px-3 py-2 text-sm ${l.code === current.code ? "border-orange-400 text-primary bg-secondary" : "border-border text-slate-600"}`}
              data-testid={`lang-option-${l.prefix || "en"}`}>
              {l.native} {l.code === current.code && <Check className="h-3.5 w-3.5 text-orange-600" />}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label={t("lang.choose")} data-testid="lang-selector"
          className="flex items-center gap-1.5 text-white/85 hover:text-white transition-colors">
          <Globe className="h-3.5 w-3.5" />
          <span lang={current.htmlLang}>{current.native}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44" data-testid="lang-menu">
        {LANGUAGES.map((l) => (
          <DropdownMenuItem key={l.code} onClick={() => switchTo(l.code)} lang={l.htmlLang}
            className="flex items-center justify-between cursor-pointer" data-testid={`lang-option-${l.prefix || "en"}`}>
            <span>{l.native}</span>
            {l.code === current.code && <Check className="h-4 w-4 text-orange-600" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
